import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Key, Copy, DollarSign, Share2 } from 'lucide-react';
import { toast } from 'sonner';

export const InfluencerOverviewTab = ({ dashboard, keys, commissions, genPlan, setGenPlan, generating, onGenerateKey, onShareClub }) => (
  <div className="space-y-6">
    {/* Stats */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center">
        <p className="text-3xl font-bold text-indigo-600">{dashboard?.total_merchants || 0}</p>
        <p className="text-xs text-slate-500 mt-1">Lojistas</p>
      </CardContent></Card>
      <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center">
        <p className="text-3xl font-bold text-green-600">{dashboard?.active_merchants || 0}</p>
        <p className="text-xs text-slate-500 mt-1">Ativos</p>
      </CardContent></Card>
      <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center">
        <p className="text-3xl font-bold text-amber-600">R$ {(dashboard?.total_commission || 0).toFixed(2)}</p>
        <p className="text-xs text-slate-500 mt-1">Comissao Total</p>
      </CardContent></Card>
      <Card className="bg-white border border-slate-200"><CardContent className="pt-5 text-center">
        <p className="text-3xl font-bold text-purple-600">{dashboard?.total_keys || 0}</p>
        <p className="text-xs text-slate-500 mt-1">Chaves Geradas</p>
      </CardContent></Card>
    </div>

    {/* Key Generator */}
    <Card className="bg-white border border-indigo-200" data-testid="key-generator-card">
      <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><Key className="h-5 w-5 text-indigo-500" /> Gerar Chave de Acesso</CardTitle></CardHeader>
      <CardContent>
        <div className="flex items-center gap-3">
          <Select value={genPlan} onValueChange={setGenPlan}>
            <SelectTrigger className="w-48" data-testid="gen-plan-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="premium">Premium - R$ 150</SelectItem>
              <SelectItem value="platinum">Platinum - R$ 300</SelectItem>
              <SelectItem value="black">Black - R$ 750</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={onGenerateKey} disabled={generating}
            className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-key-btn">
            {generating ? 'Gerando...' : 'Gerar Chave'}
          </Button>
        </div>
      </CardContent>
    </Card>

    {/* Keys List */}
    {keys.length > 0 && (
      <Card className="bg-white border border-slate-200">
        <CardHeader><CardTitle className="text-lg text-slate-900">Suas Chaves</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="text-left py-2">Chave</th><th className="text-center">Plano</th><th className="text-center">Status</th><th className="text-center">Usado Por</th>
              </tr></thead>
              <tbody>
                {keys.slice(0, 20).map(k => (
                  <tr key={k.key} className="border-b border-slate-50">
                    <td className="py-2 font-mono text-xs flex items-center gap-1">
                      {k.key}
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => { navigator.clipboard.writeText(k.key); toast.success('Copiada!'); }}>
                        <Copy className="h-3 w-3" />
                      </Button>
                    </td>
                    <td className="text-center capitalize">{k.plan}</td>
                    <td className="text-center">{k.used ? <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">Usada</Badge> : <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">Disponivel</Badge>}</td>
                    <td className="text-center text-xs text-slate-500">{k.used_by || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Commission History */}
    {commissions.length > 0 && (
      <Card className="bg-white border border-slate-200" data-testid="commissions-card">
        <CardHeader><CardTitle className="text-lg text-slate-900 flex items-center gap-2"><DollarSign className="h-5 w-5 text-green-500" /> Extrato de Comissoes</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-slate-200 text-xs text-slate-500">
                <th className="text-left py-2">Lojista</th><th className="text-center">Plano</th><th className="text-center">Valor Mensal</th><th className="text-center">Comissao</th><th className="text-center">Data</th><th className="text-center">Status</th>
              </tr></thead>
              <tbody>
                {commissions.map(c => (
                  <tr key={c.commission_id} className="border-b border-slate-50">
                    <td className="py-2">{c.company_name || c.merchant_name || '-'}</td>
                    <td className="text-center capitalize">{c.plan}</td>
                    <td className="text-center">R$ {(c.monthly_amount || 0).toFixed(2)}</td>
                    <td className="text-center font-bold text-green-600">R$ {(c.commission_amount || 0).toFixed(2)}</td>
                    <td className="text-center text-xs">{c.created_at ? new Date(c.created_at).toLocaleDateString('pt-BR') : '-'}</td>
                    <td className="text-center">
                      <Badge className={`text-xs ${c.status === 'paid' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-amber-100 text-amber-700 border-amber-200'}`}>
                        {c.status === 'paid' ? 'Pago' : 'Pendente'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    )}

    {/* Share ClubdoCupom */}
    <Card className="bg-gradient-to-r from-green-500 to-emerald-600 border-0" data-testid="share-club-card">
      <CardContent className="py-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white mb-1">Apresente o ClubdoCupom</h3>
            <p className="text-sm text-green-100">Compartilhe com seus contatos e aumente sua rede</p>
          </div>
          <Button onClick={onShareClub} className="bg-white text-green-700 hover:bg-green-50 font-bold" data-testid="share-club-btn">
            <Share2 className="h-4 w-4 mr-2" /> Compartilhar via WhatsApp
          </Button>
        </div>
      </CardContent>
    </Card>
  </div>
);
